import { Injectable } from '@angular/core';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { forEach, forOwn } from 'lodash-es';
import { HttpOperation } from '../../../../../core/enums/http-operation';
import {
  TimeBlockAbsenceModel,
  TimeBlockAppointmentModel,
  TimeBlockModel,
  TimeBlockProjectModel,
} from '../../../../../core/models/timeblock/time-block.model';
import { TimeBlockModifiedResponse } from '../../../../../shared/data-types/http-response-types';
import { DefaultDateFormat } from '../../../../../shared/localization/formats';
import { CalendarService } from '../../../services/calendar.service';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { TimeBlockItemBuilderService } from '../generation/time-block-item-builder.service';
import { TimeBlockComponent } from '../time-block.component';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import {
  TimeBlockContentType,
  TimeBlockHttpResponseType,
  TimeBlockIntervalFilter,
} from '../types/time-block-types';

@Injectable()
export class TimeBlockHttpTransformationService {
  constructor(
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly calendarService: CalendarService,
  ) {}

  public static toTimeBlockMaps(
    from: Date,
    to: Date,
    toBeTransformed: readonly ITimeBlockComponentItem[],
  ): [Map<string, ITimeBlockComponentItem[]>, Map<string, ITimeBlockComponentItem[]>] {
    const timeBlockDateArr = DateTimeHelper.eachDayOfInterval(from, to);
    const timeBlockMapFixInterval = new Map(
      timeBlockDateArr.map((date) => [
        DateTimeHelper.format(date),
        new Array<ITimeBlockComponentItem>(),
      ]),
    );

    const timeBlockMapVariableInterval = new Map(
      timeBlockDateArr.map((date) => [
        DateTimeHelper.format(date),
        new Array<ITimeBlockComponentItem>(),
      ]),
    );

    forEach(toBeTransformed, (timeBlock: ITimeBlockComponentItem) => {
      const timeBlockStartDate = DateTimeHelper.format(timeBlock.timeBlockModel.start);
      const tbMap =
        timeBlock.timeBlockModel.fixInterval !== null
          ? timeBlockMapFixInterval
          : timeBlockMapVariableInterval;
      const firstKeyOfMap = DateTimeHelper.parse(
        tbMap.keys().next().value,
        DefaultDateFormat,
        new Date(),
      );

      if (tbMap.has(timeBlockStartDate)) {
        tbMap.get(timeBlockStartDate).push(timeBlock);
      } else if (DateTimeHelper.isBefore(timeBlock.timeBlockModel.start, firstKeyOfMap)) {
        const firstKeyOfMapStr = DateTimeHelper.format(firstKeyOfMap);
        tbMap.get(firstKeyOfMapStr).push(timeBlock);
      }
    });
    return [timeBlockMapFixInterval, timeBlockMapVariableInterval];
  }

  public toTimeBlockHTTPPost(timeBlockItem: ITimeBlockComponentItem): unknown {
    const timeBlockModelHTTP = instanceToPlain(timeBlockItem.timeBlockModel);

    if ('id' in timeBlockModelHTTP) {
      delete timeBlockModelHTTP.id;
    }

    return timeBlockModelHTTP;
  }

  public toTimeBlockHTTPPut(timeBlockItem: ITimeBlockComponentItem): unknown {
    const timeBlockModelHTTP = instanceToPlain(timeBlockItem.timeBlockModel);

    if ('componentRef' in timeBlockModelHTTP) {
      delete timeBlockModelHTTP.componentRef;
    }

    if ('color' in timeBlockModelHTTP) {
      delete timeBlockModelHTTP.color;
    }

    if ('userId' in timeBlockModelHTTP) {
      delete timeBlockModelHTTP.user;
    }

    return timeBlockModelHTTP;
  }

  public toTimeBlockModifiedResponse(
    response,
    timeBlockContentType: TimeBlockContentType,
    httpOperation: HttpOperation,
  ): TimeBlockModifiedResponse {
    const modified = response.timeblocks;
    const responseCode = response.responsecode.code;

    const modifiedTimeBlockItems = this.toTimeBlockItems(
      modified,
      timeBlockContentType,
      httpOperation,
      TimeBlockIntervalFilter.All,
    );

    return {
      modifiedTimeBlockItems,
      responseCode,
    };
  }

  public toTimeBlockItems(
    timeBlocks: any[],
    timeBlockContentType: TimeBlockContentType,
    httpOperation: HttpOperation,
    intervalFilter: TimeBlockIntervalFilter,
  ): ITimeBlockComponentItem[] {
    const timeBlockArr = new Array<ITimeBlockComponentItem>();

    forOwn(timeBlocks, (timeBlock) => {
      // Filter time blocks regarding time block interval (i.e. fix interval or variable interval).
      if (
        (intervalFilter === TimeBlockIntervalFilter.FixIntervalTimeBlocks &&
          timeBlock.fix_interval === null) ||
        (intervalFilter === TimeBlockIntervalFilter.VariableIntervalTimeBlocks &&
          timeBlock.fix_interval !== null)
      ) {
        return;
      }

      let modifiedTimeBlockModel: TimeBlockModel;
      if (timeBlockContentType === TimeBlockContentType.Project) {
        modifiedTimeBlockModel = plainToInstance(TimeBlockProjectModel, timeBlock);
      } else if (timeBlockContentType === TimeBlockContentType.Absence) {
        modifiedTimeBlockModel = plainToInstance(
          TimeBlockAbsenceModel<TimeBlockComponent>,
          timeBlock,
        );
      } else if (timeBlockContentType === TimeBlockContentType.Appointment) {
        modifiedTimeBlockModel = plainToInstance(TimeBlockAppointmentModel, timeBlock);
      } else {
        throw new Error('Time block content type not supported');
      }

      const tbItem = this.timeBlockItemBuilderService.buildFromExisting(
        modifiedTimeBlockModel,
        timeBlockContentType,
      );

      // For an initial loaded active time block, the end time returned from the server stays the same. Therefore, initialize it with
      // the correct end date and time.
      if (tbItem.timeBlockModel.isActive && httpOperation === HttpOperation.Get) {
        const now = new Date();
        tbItem.timeBlockModel.end = now;

        const start = tbItem.timeBlockModel.start;
        const differenceInMinutes = DateTimeHelper.differenceInMinutes(start, now);

        // We don't have the calendar properties on app start-up yet.
        if (this.calendarService.model.calendarProperties) {
          const minimumTimeBlockDuration =
            this.calendarService.model.calendarProperties.minimumTimeBlockDuration;

          // If the difference between the start time and the current time is too small, add the minimum time block duration to the start time.
          if (differenceInMinutes < minimumTimeBlockDuration) {
            tbItem.timeBlockModel.end = DateTimeHelper.addMinutes(
              tbItem.timeBlockModel.start,
              minimumTimeBlockDuration,
            );
          }
        }
      }

      timeBlockArr.push(tbItem);
    });

    return timeBlockArr;
  }

  public mapTimeBlockResponse(response: TimeBlockHttpResponseType): [
    ITimeBlockComponentItem[], // Project type full day
    ITimeBlockComponentItem[], // Project type inner day
    ITimeBlockComponentItem[], // Absence type full day
    ITimeBlockComponentItem[], // Absence type inner day
  ] {
    const projectTimeBlocksFixInterval = this.toTimeBlockItems(
      response.timeblock_project_type,
      TimeBlockContentType.Project,
      HttpOperation.Get,
      TimeBlockIntervalFilter.FixIntervalTimeBlocks,
    );
    const projectTimeBlocksVariableInterval = this.toTimeBlockItems(
      response.timeblock_project_type,
      TimeBlockContentType.Project,
      HttpOperation.Get,
      TimeBlockIntervalFilter.VariableIntervalTimeBlocks,
    );
    const absenceTimeBlocksFixInterval = this.toTimeBlockItems(
      response.timeblock_absence_type,
      TimeBlockContentType.Absence,
      HttpOperation.Get,
      TimeBlockIntervalFilter.FixIntervalTimeBlocks,
    );
    const absenceTimeBlocksVariableInterval = this.toTimeBlockItems(
      response.timeblock_absence_type,
      TimeBlockContentType.Absence,
      HttpOperation.Get,
      TimeBlockIntervalFilter.VariableIntervalTimeBlocks,
    );

    return [
      projectTimeBlocksFixInterval,
      projectTimeBlocksVariableInterval,
      absenceTimeBlocksFixInterval,
      absenceTimeBlocksVariableInterval,
    ];
  }
}
